import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Overlay, OverlayConfig, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { NgClass } from '@angular/common';
import {
  Component,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from '@bcef/ia-components/buttons';
import { ListboxModule } from '@bcef/ia-components/listbox';
import { IconsModule } from '@bcef/ia-ng-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';

import { DocumentAttachmentGroup, TypeDocument } from '@features/succession/models';
import { SuccessionFacade } from '@features/succession/succession.facade';
import { DocumentData, DocumentUploadPopinComponent } from '@shared/components/upload';
import {
  DocumentItem,
  DocumentLabels,
} from '@shared/components/upload/document-upload-file.interface';

interface DocumentForm {
  type: FormControl<TypeDocument | null>;
}

@Component({
  selector: 'ia-succession-document-row',
  templateUrl: './document-row.component.html',
  imports: [
    ButtonModule,
    IconsModule,
    ListboxModule,
    NgClass,
    ReactiveFormsModule,
    TranslateModule,
  ],
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DocumentRowComponent,
      multi: true,
    },
  ],
})
export class DocumentRowComponent implements ControlValueAccessor, OnChanges, OnInit {
  @HostBinding('class') public classAttribute = 'tw-flex tw-flex-col sm:tw-flex-row';

  @Input() public documentAttachmentGroup: DocumentAttachmentGroup | null = null;

  @Input() public selfCareTypeDocuments: TypeDocument[] | null = null;

  @Input() public hasError = false;

  private readonly successionFacade = inject(SuccessionFacade);

  private readonly dialog = inject(Dialog);

  private readonly scrollStrategyOptions = inject(ScrollStrategyOptions);

  private readonly translate = inject(TranslateService);

  public documents: { type: TypeDocument; documents: DocumentItem[] }[] = [];

  public typeDocuments: TypeDocument[] = [];

  public documentForm: FormGroup<DocumentForm> = new FormGroup<DocumentForm>({
    type: new FormControl<TypeDocument | null>(null, [Validators.required]),
  });

  public onChange?: (value: DocumentItem[] | undefined) => void;

  public onTouched?: () => void;

  public disabled = false;

  public dialogLabels!: DocumentLabels;

  protected listBoxOverlayConfig: OverlayConfig = {
    scrollStrategy: inject(Overlay).scrollStrategies.close(),
  };

  public get typeControl(): FormControl<TypeDocument | null> {
    return this.documentForm.controls.type;
  }

  public ngOnInit(): void {
    this.translate
      .get('dialogs.document')
      .pipe(take(1))
      .subscribe((labels: DocumentLabels) => {
        this.dialogLabels = labels;
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { selfCareTypeDocuments } = changes;
    if (selfCareTypeDocuments?.currentValue) {
      if (selfCareTypeDocuments.currentValue.length === 1) {
        this.documentForm.controls.type.patchValue(selfCareTypeDocuments.currentValue[0]);
      }
      this.typeDocuments = selfCareTypeDocuments.currentValue;
    }
  }

  public writeValue(documents: DocumentItem[]): void {
    if (documents?.length) {
      this.documents = (this.selfCareTypeDocuments || [])
        .filter((type: TypeDocument) =>
          documents.some((document: DocumentItem) => document.typeDocument === type.typeDocument),
        )
        .map((type: TypeDocument) => ({
          type,
          documents: documents.filter(
            (document: DocumentItem) => document.typeDocument === type.typeDocument,
          ),
        }));

      this.typeDocuments = this.cleanTypeDocumentsList();
    }
  }

  public registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public generateDocumentDialog(typeDocument: TypeDocument): DialogRef<DocumentItem[]> {
    return this.dialog.open<DocumentItem[]>(DocumentUploadPopinComponent, {
      scrollStrategy: this.scrollStrategyOptions.block(),
      width: '700px',
      disableClose: true,
      data: {
        title: typeDocument?.label,
        description: typeDocument?.description,
        maxNumberOfDocuments: typeDocument?.isMultiple ? 4 : 1,
        attachmentGroup: this.documentAttachmentGroup?.attachmentGroup,
        token: this.successionFacade.token,
        documentType: typeDocument?.typeDocument,
        documents: this.documents
          .filter((item) => item.type.typeDocument === typeDocument?.typeDocument)
          .reduce((acc: DocumentItem[], item) => item.documents, []),
        labels: this.dialogLabels,
      } as DocumentData,
    });
  }

  public closeDocumentDialog(dialog: DialogRef<DocumentItem[]>, typeDocument: TypeDocument): void {
    dialog.closed
      .pipe(
        take(1),
        filter((documents?: DocumentItem[]) => documents !== undefined),
      )
      .subscribe((documents?: DocumentItem[]) => {
        this.onChange?.(documents);
        this.onTouched?.();
        const hasDocuments: boolean = this.documents.some(
          (item) => item.type.typeDocument === typeDocument?.typeDocument,
        );
        this.documents = hasDocuments
          ? this.documents.map((item) =>
              item.type.typeDocument === typeDocument?.typeDocument
                ? {
                    ...item,
                    documents: documents || [],
                  }
                : item,
            )
          : [
              ...this.documents,
              {
                type: typeDocument,
                documents: documents || [],
              },
            ];

        this.typeDocuments = this.cleanTypeDocumentsList();
        if ((this.selfCareTypeDocuments?.length || 0) > 1) {
          this.documentForm.patchValue({ type: null });
        }
      });
  }

  public openDocumentDialog(selfCareTypeDocument?: TypeDocument): void {
    if (this.documentForm.invalid && !selfCareTypeDocument) {
      return;
    }
    const typeDocument: TypeDocument =
      selfCareTypeDocument ?? (this.typeControl.value as TypeDocument);

    const dialog = this.generateDocumentDialog(typeDocument);
    this.closeDocumentDialog(dialog, typeDocument);
  }

  public cleanTypeDocumentsList(): TypeDocument[] {
    return (
      this.selfCareTypeDocuments?.filter(
        (item: TypeDocument) =>
          !this.documents.some(
            (group) => group.type.typeDocument === item.typeDocument && group.documents.length,
          ),
      ) || []
    );
  }
}
